import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useParams, useHistory, Link } from 'react-router-dom';
import { CancelTokenSource } from 'axios';
import { RootState } from 'modules';
import { showConfirm } from 'components/modal/showConfirm';
import apiClient from 'libs/api/apiClient';
import { getSubThemeList } from 'libs/api/learning';
import { usePermission, useDataApi } from 'libs/hooks';
import { TSubTheme, TLearningParams } from 'modules/learning';
import { CWindowWidth } from 'constants/base.const';
import { ENotificationType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Row, Button } from 'antd';
import styled from 'styled-components';
import { device } from 'GlobalStyles';
import ExamLinkBtn from '../parts/ExamLinkBtn';
import ExplainTemplate from '../parts/ExplainTemplate';
import RecommendCardBox from '../parts/RecommendCardBox';
import RecommendSubjectBox from '../parts/RecommendSubject';
import LearningBoardEditor from './LearningBoardEditor';

const ExampleDetail = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [learningExample, setLearningExample] = useState<TSubTheme>();
  const { category, step_id, example_id, operation }: TLearningParams = useParams();
  const { adminPermission } = usePermission();
  const history = useHistory();

  const getLearningExample = async () => {
    const { success, data } = await getSubThemeList(example_id);

    if (success) setLearningExample(data);
  };

  useEffect(() => {
    if (!currentUser?.primary_field) {
      // testing version
      currentUser?.primary_field !== 'finance' && getLearningExample();
      // testing version

      return;
    } else if (category !== currentUser?.primary_field) {
      let getPrimaryField =
        category === 'fit_interview' ||
        category === 'turnaround_strategy' ||
        category === 'case_interview' ||
        category === 'growth_strategy' ||
        category === 'dd_market_sizing' ||
        category === 'operation_optimization' ||
        category === 'sales_marketing_strategy'
          ? 'consulting'
          : 'finance';

      if (currentUser?.primary_field !== getPrimaryField) {
        history.push(`${ERoute.CSLearningBoard}`);
      } else {
        // testing version
        currentUser?.primary_field !== 'finance' && getLearningExample();
        // testing version

        //main version
        // getSubThemes();
      }
    }
  }, [example_id, operation]);

  const deleteBoard = (id: string, cancel?: CancelTokenSource) => {
    return apiClient.delete(`/drill/board/${id}`, {
      cancelToken: cancel?.token,
    });
  };

  async function onClickDelete() {
    try {
      await deleteBoard(example_id);

      history.push({
        pathname: `${ERoute.CSLearningBoard}/${category}/view`,
        state: {
          notification: {
            type: ENotificationType.Success,
            message: '게시글이 삭제되었습니다.',
          },
        },
      });
    } catch (e) {
      throw e;
    }
  }

  const getDetail = (id: string, cancel?: CancelTokenSource) => {
    return apiClient.get(`/drill/board/${id}`, {
      cancelToken: cancel?.token,
    });
  };

  const [{ data }] = useDataApi(getDetail.bind(null, example_id), {}, operation !== 'create');

  return (
    <>
      {operation !== 'edit' && learningExample && (
        <ExplainTemplate
          img={learningExample.thumbnail.location}
          pageTitle={learningExample.title}
          pageSubtitle={learningExample.subtitle}
          hasSubject={true}
          subject={learningExample.subject}
        >
          {learningExample.exercise_button === 'top' && (
            <StyledInputForm>
              {learningExample.quiz_id && <ExamLinkBtn content={learningExample.quiz_id} />}
              <StyledParagraph
                dangerouslySetInnerHTML={{
                  __html: `${learningExample.body_list![0].body.replaceAll('Malgun Gothic', 'DMSans')}`,
                }}
              />
            </StyledInputForm>
          )}
          {learningExample.exercise_button === 'middle' && (
            <StyledInputForm>
              <StyledParagraph
                dangerouslySetInnerHTML={{
                  __html: `${learningExample.body_list![0].body.replaceAll('Malgun Gothic', 'DMSans')}`,
                }}
              />
              {learningExample.quiz_id && <ExamLinkBtn content={learningExample.quiz_id} />}
              <StyledParagraph
                dangerouslySetInnerHTML={{
                  __html: `${learningExample.body_list![1].body.replaceAll('Malgun Gothic', 'DMSans')}`,
                }}
              />
            </StyledInputForm>
          )}
          {learningExample.exercise_button === 'bottom' && (
            <StyledInputForm>
              <StyledParagraph
                dangerouslySetInnerHTML={{
                  __html: `${learningExample.body_list![0].body.replaceAll('Malgun Gothic', 'DMSans')}`,
                }}
              />
              {learningExample.quiz_id && <ExamLinkBtn content={learningExample.quiz_id} />}
            </StyledInputForm>
          )}

          {adminPermission && (
            <Row>
              <Button
                type='primary'
                size='large'
                onClick={() => showConfirm(() => onClickDelete(), '게시글을 삭제하시겠습니까?')}
                style={{ marginRight: '20px' }}
              >
                삭제
              </Button>
              <Link to={`${ERoute.CSLearningBoard}/${category}/${step_id}/${example_id}/edit`}>
                <Button type='primary' size='large'>
                  수정
                </Button>
              </Link>
            </Row>
          )}

          <RecommendSubjectBox />
          <RecommendCardBox list={learningExample.example} title='주제와 관련된 다른 예제 읽어보기' />
        </ExplainTemplate>
      )}
      {operation === 'edit' && (
        <LearningBoardEditor
          pathName={`${ERoute.CSLearningBoard}/${category}/${step_id}/view`}
          operation={operation}
          isEdit={true}
          record={data}
          isExample={true}
        />
      )}
    </>
  );
};

export default ExampleDetail;

const maxWidth = CWindowWidth - 300;
const StyledInputForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledParagraph = styled.div`
  max-width: ${maxWidth};
  width: 55%;
  margin-top: 50px;
  -webkit-user-drag: none;
  -webkit-touch-callout: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  @media screen and ${device.xl} {
    width: 600px;
  }

  @media screen and ${device.lg} {
    width: 560px;
  }

  @media screen and ${device.md} {
    width: 480px;
  }

  @media screen and ${device.sm} {
    width: 310px;
  }

  img {
    max-width: 100%;
    min-width: 650px;
    height: auto;

    @media screen and ${device.lg} {
      width: 560px !important;
      min-width: 0px;
      height: auto;
    }

    @media screen and ${device.md} {
      width: 480px !important;
      height: auto;
    }

    @media screen and ${device.sm} {
      width: 310px !important;
      height: auto;
    }
  }

  iframe {
    max-width: 650px;
    max-height: 364px;

    @media screen and ${device.xl} {
      width: 560px !important;
      height: 314px !important;
    }

    @media screen and ${device.lg} {
      width: 480px !important;
      height: 268px !important;
    }

    @media screen and ${device.md} {
      width: 400px !important;
      height: 224px !important;
    }

    @media screen and ${device.sm} {
      width: 300px !important;
      height: 168px !important;
    }
  }
`;
